import { spawn } from 'node:child_process';
import fs from 'node:fs';
import { tmpdir } from 'node:os';
import path from 'node:path';
import type { PreviewEnvironmentProcessLifecycle } from '../process-lifecycle.js';
import type { ShellSpawnResult } from './types.js';
import { isSpawnEbadf, mergePreviewEnvironmentSpawnOptions, rmDirQuiet } from './utils.js';

/** POSIX single-quoted string for embedding in `sh` scripts. */
function shSingleQuote(s: string): string {
  return `'${s.replace(/'/g, `'\\''`)}'`;
}

/**
 * On-disk wrapper script + `spawn(..., stdio: 'ignore')` so Node never creates pipe endpoints.
 */
export function trySpawnShellScriptLogRedirectUnix(
  command: string,
  env: NodeJS.ProcessEnv,
  cwd: string,
  signal?: AbortSignal,
  lifecycle?: PreviewEnvironmentProcessLifecycle,
): ShellSpawnResult | null {
  const tmpRoot = fs.mkdtempSync(path.join(tmpdir(), 'ba-devsrv-sh-'));
  const logPath = path.join(tmpRoot, 'combined.log');
  const innerPath = path.join(tmpRoot, '_cmd.sh');
  const runnerPath = path.join(tmpRoot, '_run.sh');
  try {
    fs.writeFileSync(innerPath, `#!/bin/sh\n${command}\n`);
    fs.writeFileSync(
      runnerPath,
      `#!/bin/sh\ncd ${shSingleQuote(cwd)}\n/bin/sh ${shSingleQuote(innerPath)} >>${shSingleQuote(logPath)} 2>&1\n`,
    );
    const proc = spawn(
      '/bin/sh',
      [runnerPath],
      mergePreviewEnvironmentSpawnOptions(
        { env, cwd: tmpRoot, stdio: 'ignore', ...(signal ? { signal } : {}) },
        lifecycle,
      ),
    );
    return {
      proc,
      pipedStdoutStderr: true,
      mergedLogPath: logPath,
      mergedLogCleanupDir: tmpRoot,
    };
  } catch (e) {
    rmDirQuiet(tmpRoot);
    if (isSpawnEbadf(e)) return null;
    throw e;
  }
}

export function trySpawnShellScriptLogRedirectWin(
  command: string,
  env: NodeJS.ProcessEnv,
  cwd: string,
  signal?: AbortSignal,
  lifecycle?: PreviewEnvironmentProcessLifecycle,
): ShellSpawnResult | null {
  const tmpRoot = fs.mkdtempSync(path.join(tmpdir(), 'ba-devsrv-sh-'));
  const logPath = path.join(tmpRoot, 'combined.log');
  const runnerPath = path.join(tmpRoot, '_run.bat');
  const q = (p: string) => `"${p.replace(/"/g, '""')}"`;
  const com = process.env.ComSpec || 'cmd.exe';
  try {
    fs.writeFileSync(
      runnerPath,
      `@ECHO OFF\r\nCD /D ${q(cwd)}\r\n${command} >> ${q(logPath)} 2>&1\r\n`,
    );
    const proc = spawn(
      com,
      ['/d', '/s', '/c', q(runnerPath)],
      mergePreviewEnvironmentSpawnOptions(
        { env, cwd: tmpRoot, stdio: 'ignore', windowsHide: true, ...(signal ? { signal } : {}) },
        lifecycle,
      ),
    );
    return {
      proc,
      pipedStdoutStderr: true,
      mergedLogPath: logPath,
      mergedLogCleanupDir: tmpRoot,
    };
  } catch (e) {
    rmDirQuiet(tmpRoot);
    if (isSpawnEbadf(e)) return null;
    throw e;
  }
}
